<!--

  ActionDialog.vue
  Componente reutilizável de diálogo de ações, confirmações ou alertas.

  Ele aceita título, texto e botões de ação personalizáveis, funcionando de 
  forma reativa através do v-model. Pode ser usado tanto para mensagens simples
  quanto para confirmações com dois botões.

  É possível definir cores, variantes e rótulos dos botões, ocultar qualquer um 
  deles, e ajustar a largura do card. Quando `showDefaultActions` é ativado, o 
  footer padrão é exibido com os botões conforme as propriedades informadas.  

  O componente foi pensado para manter a consistência visual dos
  diálogos do projeto, simplificando o uso e reduzindo duplicação de código.
  Ele também permite adicionar conteúdo customizado via slot quando necessário,
  mantendo sobre título, texto e ações.

  Exemplo de uso:

  <ActionDialog
    v-model:is-open="isExemplo"
    :title="Title"
    :text="Texto de exemplo"
    :max-width="'500px'"
    :persistent="true"
    :show-default-actions="true"
    :confirm-label="Confirm"
    :cancel-label="Cancel"
    confirm-color="error"
    confirm-variant="flat"
    cancel-variant="text"
    @confirm="confirm"
  />

-->

<script setup lang="ts">
import { computed } from 'vue'
import { VBtn } from 'vuetify/components'
type VVariant = 'elevated' | 'flat' | 'tonal' | 'outlined' | 'text' | 'plain'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirm'): void
  (e: 'update:isOpen', value: boolean): void
}>()

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  isOpen: boolean
  persistent?: boolean
  maxWidth?: string
  title?: string
  text?: string
  classTitle?: string
  classText?: string
  showClose?: boolean
  showDefaultActions?: boolean
  confirmLabel?: string
  confirmLoading?: boolean
  confirmColor?: string
  confirmVariant?: VVariant
  cancelLabel?: string
  cancelColor?: string
  cancelVariant?: VVariant
  hideConfirm?: boolean
  hideCancel?: boolean
}>()

const dialogModel = computed({
  get: () => props.isOpen,
  set: (val: boolean) => emit('update:isOpen', val),
})

const showCancel = computed(
  () => !(props.hideCancel === true) && !(props.cancelLabel === '')
)
const showConfirm = computed(() => !(props.hideConfirm === true))

function handleClose() {
  emit('close')
  emit('update:isOpen', false)
}

function handleConfirm() {
  emit('confirm')
}
</script>

<template>
  <v-dialog
    :model-value="dialogModel"
    :persistent="persistent ?? false"
    :max-width="maxWidth ?? '500px'"
    @update:model-value="(v: boolean) => (dialogModel = v)"
  >
    <v-card class="elevation-0 pa-0">
      <div class="d-flex align-center justify-space-between px-6 pb-2 pt-4">
        <div v-if="title" class="text-h6 p-0" :class="classTitle">
          {{ title }}
        </div>

        <v-btn
          v-if="showClose !== false"
          icon="mdi-close"
          variant="text"
          class="ml-2"
          aria-label="Fechar"
          @click="handleClose"
        />
      </div>

      <div class="px-6 pb-2">
        <div v-if="text" class="bodyText" :class="classText">
          {{ text }}
        </div>
        <slot />
      </div>

      <div v-if="showDefaultActions" class="d-flex justify-end gap-2 px-4 py-2">
        <v-btn
          v-if="showCancel"
          :color="cancelColor"
          :variant="cancelVariant"
          class="text-none font-weight-bold"
          @click="handleClose"
        >
          {{ cancelLabel }}
        </v-btn>

        <v-btn
          v-if="showConfirm"
          :color="confirmColor"
          :variant="confirmVariant"
          class="text-none font-weight-bold"
          :loading="confirmLoading"
          @click="handleConfirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.text-h6 {
  color: rgba(0, 0, 0, 0.87);
  font-family: 'Open Sans';
  font-size: 20px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.05px;
}

.bodyText {
  color: rgba(0, 0, 0, 0.6);
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.014px;
}

.v-btn {
  height: 36px !important;
  padding: 0 8px !important;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.125px;
}

.bg-primary {
  color: white !important;
}

@media (max-width: 600px) {
  .text-h6 {
    text-wrap: wrap;
  }
}
</style>
